import { useFeatures } from '@kmx/io-core/context/FeaturesContext';
import { createMeasurement, ICOPerformanceMarks } from '@kmx/io-core/utils/performance';
import { Button as KmxButton } from '@kmx/legos-react-button';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import classNames from 'classnames';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { isIOS, isMobile } from 'react-device-detect';
import { getWalletPassV2 } from '../../api/walletPass';
import Swoosh from '../../assets/swoosh.png';
import WalletPassImage from '../../assets/wallet_pass.svg';
import { useClientInfo } from '../../context/clientInfo';
import { useOfferContext } from '../../context/offerContext';
import { testNames } from '../../types/testNames';
import { updateSourceSection, withAnalyticsClickTracking } from '../../utils/analytics';
import { formatSeperatedInteger } from '../../utils/format';
import { goToCarSearch, goToMyKmx, goToPreQual, setAppointment } from '../../utils/redirect';
import Card from '../Card';
import ICODialog from '../ICODialog';
import KbbValuation from './KbbValuation';

import * as styles from './Offer.module.scss';

const Button = withAnalyticsClickTracking(KmxButton);

interface IOfferProps {
    className?: string;
    vin: string;
    originPage?: string;
    userId: string;
    storeId?: string;
    onEditVehicleClick: () => void;
}

const OFFER_PAGE_ID = 'Offer Page';

const Offer: React.FC<IOfferProps> = props => {
    // const { customerInfo } = useCustomerInfo();
    const appInsights = useAppInsightsContext();
    const [continueModalOpen, setContinueModalOpen] = useState(false);
    const { offer, kbb } = useOfferContext();
    const { clientInfo } = useClientInfo();
    const { isFeatureEnabled } = useFeatures();

    useEffect(() => {
        performance.mark(ICOPerformanceMarks.WidgetOfferViewed);
        const measure = createMeasurement(ICOPerformanceMarks.SubmitClick, ICOPerformanceMarks.WidgetOfferViewed, {
            offerId: offer.code,
        });
        if (measure) {
            appInsights.trackEvent({
                name: 'performance: submit_to_view',
                properties: {
                    offerId: offer.code,
                    duration: measure.duration,
                },
            });
        }
        appInsights.trackEvent({
            name: 'Offer page - viewed',
            properties: {
                quoteId: offer.code,
                vin: props.vin,
            },
        });
        // Update source section when the offer page loads
        updateSourceSection('Offer Page');
    }, []);

    const ctaValues = {
        headline: `We're ready to buy your car`,
        description: `Bring your car and offer to any CarMax store.\nWe'll confirm your car's condition and pay you on the spot.`,
        buttons: [
            { header: 'Continue', content: '' },
            ...(props.userId ? [] : [{ header: 'Save this offer', content: '' }]),
        ],
    };

    if (!offer) {
        return null;
    }

    const continueClick = () => {
        setAppointment(props.originPage ?? '', clientInfo, offer);
    };

    const getHeadlineText = () => {
        return ctaValues.headline;
    };

    const getDescriptionText = () => {
        return ctaValues.description;
    };

    const continueDialog = (
        <ICODialog closeDialog={() => setContinueModalOpen(false)} showDefaultCloseButton={false}>
            <div>
                <h3 className="kmx-typography--display-4">What do you want to do next?</h3>
                <Card
                    id="next-steps-schedule-appt"
                    header="Schedule my appointment"
                    content="Bring your car and offer to any CarMax store. We'll confirm your car's condition and pay you on the spot."
                    onClick={() => setAppointment(props.originPage, clientInfo, offer)}
                />
                <Card
                    id="next-steps-prequal"
                    header="Get pre-qualified"
                    content="Get financing results without impacting your credit and see personalized terms on our cars."
                    onClick={goToPreQual}
                />
                <Card
                    id="next-steps-shop"
                    header="Shop for my next car"
                    content="Now that you know what you will recieve for your trade-in, use it to shop for another vehicle."
                    onClick={goToCarSearch}
                />
            </div>
        </ICODialog>
    );

    return (
        <div data-qa="offer-page" id={OFFER_PAGE_ID}>
            {continueModalOpen && continueDialog}
            <div className={classNames('kmx-ico-offer', styles.offerContainer, props.className)}>
                <div className={classNames('kmx-ico-offer-offerinfo', styles.offerInfo)}>
                    <h3 className="kmx-typography--display-4">Your Offer</h3>
                    <h1 data-qa="offer-amount" className="kmx-typography--display-6">
                        ${formatSeperatedInteger(offer.valuation)}
                    </h1>
                    <img className={styles.swoosh} src={process.env.BASE_URL + Swoosh} role="none" />
                    <p className={classNames(styles.offerCode, 'kmx-typography--display-3')}>
                        Offer code: {offer.code}
                    </p>
                    <p className={classNames(styles.expiration, 'kmx-typography--body-1')}>
                        Valid through {offer.expiresDisplayDate}
                    </p>
                    <div className={styles.nextActionsContainer}>
                        {isFeatureEnabled(testNames.WALLET_PASS) && isIOS && isMobile && (
                            <div id="wallet-pass">
                                <button
                                    id="wallet-pass"
                                    className={styles.walletPassButton}
                                    onClick={() => getWalletPassV2(offer.code)}
                                >
                                    <WalletPassImage className={styles.walletPass} />
                                    wallet-pass
                                </button>
                            </div>
                        )}
                        <Button level="primary" className={styles.offerCta} onClick={continueClick}>
                            Continue
                        </Button>
                    </div>
                </div>
                {offer?.metaData?.offerDisclaimer && (
                    <div className={styles.offerDisclaimer}>
                        <p className={classNames('kmx-typography--body-2', styles.disclaimerText)}>
                            {offer.metaData.offerDisclaimer}
                        </p>
                    </div>
                )}
                {kbb && <KbbValuation />}
                <>
                    <div
                        id="kmx-ico-offer-appointment"
                        className={classNames('kmx-ico-offer-appointment', styles.appointmentContainer)}
                    >
                        <h3 className={classNames('kmx-typography--display-3', styles.headline)}>
                            {getHeadlineText()}
                        </h3>
                        <p className={classNames('kmx-typography--body-2', styles.description)}>
                            {getDescriptionText()}
                        </p>
                        {!props.userId && (
                            <Button
                                id="ico-save-offer"
                                onClick={() => goToMyKmx(offer)}
                                level={'tertiary'}
                                className={styles.saveOffer}
                            >
                                {'Save this offer'}
                            </Button>
                        )}
                    </div>
                </>
            </div>
        </div>
    );
};

export default Offer;
